import React, { Component } from 'react';
import PropTypes from 'prop-types';
import 'react-dates/initialize';
import 'react-dates/lib/css/_datepicker.css';
import DateRangePicker from 'react-dates/lib/components/DateRangePicker';
import { ANCHOR_RIGHT, ANCHOR_LEFT } from 'react-dates/constants';
import moment from 'moment/min/moment-with-locales';
import { END_DATE, START_DATE } from './calendar-constants';
import DateHelper from '../../../../helpers/date-helper';

/**
 * Calendar component
 * Renders a calendar using react-dates library https://github.com/airbnb/react-dates
 * Limits what dates a user can select to meet business rules
 * Calls onDatesPicked prop when user selects both a start and end date, calls onClose prop
 * when the react-dates calendar component is closed for any reason
 */
class Calendar extends Component {
    static propTypes = {
        /** Range to limit the date selection to */
        range: PropTypes.shape({
            startDate: PropTypes.string,
            endDate: PropTypes.string
        }).isRequired,

        /** Callback prop when dates are selected */
        onDatesPicked: PropTypes.func.isRequired,

        /** Callback prop when calendar is closed, either from selecting dates or cancelling date selection */
        onClose: PropTypes.func.isRequired,

        /**
         * Locale Data
         */
        localeData: PropTypes.object,
        /**
         * Direction of picker
        * */
        isLeft: PropTypes.bool
    };

    constructor(props) {
        super(props);
        this.state = {
            focusedInput: START_DATE,
            startDate: null,
            endDate: null,
            range: {
                startDate: moment(props.range.startDate),
                endDate: moment(props.range.endDate)
            }
        };
    }

    /**
     * Limit what days users can select in the calendar
     * Start date has to be a Monday and end date a Sunday, both dates have to be within
     * the range provided via props
     * @param date - moment date object
     */
    isOutsideRange = (date) => {
        const { focusedInput, range } = this.state;
        const { localeData } = this.props;
        if (date.isAfter(range.startDate) && date.isBefore(range.endDate)) {
            let allowedDay;
            if (focusedInput === START_DATE) {
                allowedDay = localeData['Label - Monday'];
            } else {
                allowedDay = localeData['Label - Sunday'];
            }
            return date.format('dddd') !== allowedDay;
        }

        return true;
    };

    onDatesChange = ({ startDate, endDate }) => {
        if (this.state.focusedInput === END_DATE && !endDate) {
            // user has selected an end date earlier than the start date,
            // auto select the prev Monday as the start date and set the end date as
            // they chose
            endDate = startDate;
            startDate = moment(DateHelper.getPreviousMonday(startDate));
        }
        this.setState({ startDate, endDate });
        if (startDate && endDate) {
            this.props.onDatesPicked(
                startDate.format('YYYY-MM-DD'),
                endDate.format('YYYY-MM-DD')
            );
        }
    };

    onFocusChange = (elementId) => {
        this.setState({ focusedInput: elementId });
    };

    render() {
        const { startDate, endDate, focusedInput } = this.state;
        const { localeData, isLeft } = this.props;
        const startDateLabel = localeData['Label - StartDate'];
        const endDateLabel = localeData['Label - EndDate'];
        return (
            <div>
                <DateRangePicker
                    displayFormat="DD MMM YYYY"
                    isOutsideRange={this.isOutsideRange}
                    startDate={startDate}
                    startDateId={START_DATE}
                    startDatePlaceholderText={startDateLabel}
                    endDate={endDate}
                    endDateId={END_DATE}
                    endDatePlaceholderText={endDateLabel}
                    onDatesChange={this.onDatesChange}
                    focusedInput={focusedInput}
                    onFocusChange={this.onFocusChange}
                    firstDayOfWeek={1}
                    hideKeyboardShortcutsPanel
                    minimumNights={0}
                    anchorDirection={isLeft ? ANCHOR_LEFT : ANCHOR_RIGHT}
                    onClose={this.props.onClose}
                    readOnly
                />
            </div>
        );
    }
}

Calendar.START_DATE = START_DATE;
Calendar.END_DATE = END_DATE;

export default Calendar;



// WEBPACK FOOTER //
// ./components/date-picker/components/calendar/calendar.js